import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCheck, FaInfoCircle } from 'react-icons/fa';
import useScrollAnimation from './useScrollAnimation';

const finishes = [
  {
    id: 'natural',
    name: 'Natural Titanium',
    hex: '#9e9992',
    accentGradient: 'linear-gradient(135deg, #b8b3ab, #7a7670)',
    description: 'Forged from Grade 5 aerospace-grade titanium with a precision micro-blasted satin texture.',
    image: '/Photo/i7 air 2.jpg'
  },
  {
    id: 'blue',
    name: 'Blue Titanium',
    hex: '#3b4d61',
    accentGradient: 'linear-gradient(135deg, #4d647e, #273646)',
    description: 'A deep, refined metallic ocean blue finish with a vapor-deposited PVD protective coating.',
    image: '/Photo/i17air-1.jpg'
  },
  {
    id: 'white',
    name: 'White Titanium',
    hex: '#e2e4e1',
    accentGradient: 'linear-gradient(135deg, #ffffff, #c2c5c0)',
    description: 'Crisp, radiant titanium with high-purity frosted matte ceramic glass backing.',
    image: '/Photo/SC26 back .jpg'
  },
  {
    id: 'black',
    name: 'Black Titanium',
    hex: '#2e3033',
    accentGradient: 'linear-gradient(135deg, #44474d, #191a1c)',
    description: 'Stealth dark finish engineered with diamond-like carbon (DLC) coating for maximum scratch resistance.',
    image: '/Photo/17pro max .jpg'
  }
];

const ProductShowcase = () => {
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.15 });
  const [selectedFinish, setSelectedFinish] = useState(finishes[0]);
  const [activeHotspot, setActiveHotspot] = useState(null);

  const hotspots = [
    {
      id: 'titanium',
      title: 'Grade 5 Titanium Frame',
      desc: 'Precision bonded to an internal aluminum substructure using state-of-the-art solid-state diffusion.',
      x: '22%',
      y: '32%'
    },
    {
      id: 'camera',
      title: '48MP Quad-Pixel Sensor',
      desc: 'Next-gen sensor with 100% Focus Pixels and 2nd-gen sensor-shift optical image stabilization.',
      x: '75%',
      y: '28%'
    },
    {
      id: 'action',
      title: 'Haptic Action Button',
      desc: 'Programmable button replacing the single-function switch with dynamic Island feedback.',
      x: '18%',
      y: '65%'
    }
  ];

  return (
    <section 
      ref={sectionRef} 
      className={`showcase-section ${isVisible ? 'is-visible' : ''}`}
      id="showcase"
    >
      <div className="section-container">
        {/* Section Header */}
        <div className="text-center showcase-header scroll-fade-up">
          <span className="section-eyebrow">Closer Look</span>
          <h2 className="section-headline">Explore the design.</h2>
          <p className="section-subheadline">
            Peace i17 Air is the first Peace smartphone to feature an aerospace-grade titanium design.
          </p>
        </div>

        {/* Interactive Device Stage */}
        <div className="showcase-stage scroll-scale-up">
          <div className="showcase-viewport">
            {/* Ambient dynamic radial glow matching selected finish */}
            <div 
              className="showcase-finish-glow" 
              style={{ background: selectedFinish.accentGradient }}
            />

            {/* Device Image with Smooth Scale and Fade */}
            <div className="showcase-image-wrapper">
              <img 
                src={selectedFinish.image} 
                alt={`Peace i17 Air in ${selectedFinish.name}`}
                className="showcase-main-image"
                key={selectedFinish.id}
              />

              {/* Interactive Hotspots */}
              {hotspots.map((spot) => (
                <div
                  key={spot.id}
                  className={`hotspot-marker ${activeHotspot?.id === spot.id ? 'active' : ''}`}
                  style={{ left: spot.x, top: spot.y }}
                  onClick={() => setActiveHotspot(activeHotspot?.id === spot.id ? null : spot)}
                  title={spot.title}
                >
                  <span className="hotspot-pulse" />
                  <span className="hotspot-dot">+</span>

                  {activeHotspot?.id === spot.id && (
                    <div className="hotspot-tooltip">
                      <div className="tooltip-title">{spot.title}</div>
                      <div className="tooltip-desc">{spot.desc}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Color Switcher Controls */}
          <div className="showcase-controls scroll-fade-up">
            <div className="finish-name-label">
              <span>Finish: </span>
              <strong>{selectedFinish.name}</strong>
            </div>

            <div className="color-swatches-row" role="radiogroup" aria-label="Device colors">
              {finishes.map((finish) => {
                const isSelected = selectedFinish.id === finish.id;
                return (
                  <button
                    key={finish.id}
                    onClick={() => {
                      setSelectedFinish(finish);
                      setActiveHotspot(null);
                    }}
                    className={`color-swatch-btn ${isSelected ? 'selected' : ''}`}
                    style={{ backgroundColor: finish.hex }}
                    aria-label={finish.name}
                    aria-checked={isSelected}
                    role="radio"
                  >
                    {isSelected && <FaCheck className="swatch-check" />}
                  </button>
                );
              })}
            </div>

            <p className="finish-description">
              {selectedFinish.description}
            </p>

            <div className="showcase-cta-bar">
              <Link to="/products" className="apple-btn apple-btn-primary">
                Pre-order {selectedFinish.name}
              </Link>
              <span className="showcase-hint">
                <FaInfoCircle className="hint-icon" /> Click '+' on the phone to inspect hardware features
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
